import imageCompression from "browser-image-compression";
import React, { useCallback, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import userPlaceholder from "../../../assets/images/userPlaceholder.png";
import ClickIcon from "../../../components/essentials/icon-buttons/click-icon/ClickIcon";

import DeleteButton from "../../../components/essentials/icon-buttons/delete-button/DeleteButton";
import "./CompleteProfilePicture.scss";

// Converts the cropped area into a Blob for uploading
const getCroppedImg = (
	imageSrc: string,
	cropAreaPixels: any
): Promise<Blob> => {
	return new Promise((resolve, reject) => {
		const image = new Image();
		image.crossOrigin = "anonymous";
		image.src = imageSrc;

		image.onload = () => {
			const MAX_DIMENSION = 300; // target size for width/height
			const scaleRatio =
				MAX_DIMENSION / Math.max(cropAreaPixels.width, cropAreaPixels.height);

			const canvas = document.createElement("canvas");
			canvas.width = cropAreaPixels.width * scaleRatio;
			canvas.height = cropAreaPixels.height * scaleRatio;
			const ctx = canvas.getContext("2d");

			if (!ctx) {
				reject("Could not get canvas context");
				return;
			}

			ctx.drawImage(
				image,
				cropAreaPixels.x,
				cropAreaPixels.y,
				cropAreaPixels.width,
				cropAreaPixels.height,
				0,
				0,
				canvas.width,
				canvas.height
			);

			// Use JPEG and reduce quality to 0.7
			canvas.toBlob(
				(blob) => {
					if (blob) resolve(blob);
					else reject("Canvas is empty");
				},
				"image/jpeg",
				0.7 // quality between 0 and 1
			);
		};

		image.onerror = (error) => {
			reject(error);
		};
	});
};

export interface SelectProfilePictureProps {
	onSave: (croppedBlob: Blob | null) => void;
	onCancel?: () => void;
	initialImageUrl?: string | null;
	isTouch?: boolean;
}

const SelectProfilePicture: React.FC<SelectProfilePictureProps> = ({
	onSave,
	onCancel,
	initialImageUrl,
	isTouch = false,
}) => {
	const fileInputRef = useRef<HTMLInputElement>(null);
	const imageSrcBlobRef = useRef<string | null>(null);
	const croppedPreviewBlobRef = useRef<string | null>(null);

	const setImageSrcBlob = (url: string) => {
		if (imageSrcBlobRef.current) URL.revokeObjectURL(imageSrcBlobRef.current);
		imageSrcBlobRef.current = url;
		setImageSrc(url);
	};

	const setCroppedPreviewBlob = (url: string | null) => {
		if (croppedPreviewBlobRef.current) URL.revokeObjectURL(croppedPreviewBlobRef.current);
		croppedPreviewBlobRef.current = url;
		setCroppedPreview(url);
	};

	const [imageSrc, setImageSrc] = useState<string | undefined>(
		initialImageUrl ?? userPlaceholder
	);
	const [zoom, setZoom] = useState<number>(1);
	const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
	const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
	const [isCropping, setIsCropping] = useState(false);
	const [croppedPreview, setCroppedPreview] = useState<string | null>(null);
	const [croppedBlob, setCroppedBlob] = useState<Blob | null>(null);
	const [hasChanges, setHasChanges] = useState(false);

	const [isHovered, setIsHovered] = useState<boolean>(false);

	const [hasImage, setHasImage] = useState<boolean>(!!initialImageUrl);

	const [touchNumber, setTouchNumber] = useState<number>(0);

	// Open file input when image is clicked
	const handleImageClick = () => {
		if (isTouch && touchNumber <= 1) {
			return;
		}
		setIsHovered(false);
		fileInputRef.current?.click();
	};

	// Load selected image and enter cropping mode
	const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files?.[0];
		if (!file) return;

		try {
			// First compress the image before cropping
			const options = {
				maxSizeMB: 2,
				maxWidthOrHeight: 2048,
				useWebWorker: true,
			};
			const compressedFile = await imageCompression(file, options);
			const url = URL.createObjectURL(compressedFile);

			const img = new Image();
			img.src = url;
			img.onload = () => {
				if (img.width < 300 || img.height < 300) {
					alert("Image must be at least 300x300 pixels.");
					URL.revokeObjectURL(url);
					return;
				}

				setIsCropping(true);
				setZoom(1);
				setCrop({ x: 0, y: 0 });
				setCroppedPreviewBlob(url);
				setCroppedBlob(null);
				setHasChanges(true);
			};
		} catch (err) {
			console.error("Image compression failed:", err);
			// Fallback to original file if compression fails
			const url = URL.createObjectURL(file);
			const img = new Image();
			img.src = url;
			img.onload = () => {
				if (img.width < 300 || img.height < 300) {
					alert("Image must be at least 300x300 pixels.");
					URL.revokeObjectURL(url);
					return;
				}

				setIsCropping(true);
				setZoom(1);
				setCrop({ x: 0, y: 0 });
				setCroppedPreviewBlob(url);
				setCroppedBlob(null);
				setHasChanges(true);
			};
		}
	};

	// Called when cropping completes
	const onCropComplete = useCallback((_: any, croppedPixels: any) => {
		setCroppedAreaPixels(croppedPixels);
	}, []);

	// User confirms crop — generate preview and store blob for final save
	const handleCropConfirm = async () => {
		setHasImage(true);
		setIsHovered(false);

		if (croppedPreview && croppedAreaPixels) {
			try {
				const blob = await getCroppedImg(croppedPreview, croppedAreaPixels);
				const previewUrl = URL.createObjectURL(blob);
				setImageSrcBlob(previewUrl);
				setCroppedPreviewBlob(null);
				setCroppedBlob(blob);
				setIsCropping(false);
				if (fileInputRef.current) {
					fileInputRef.current.value = "";
				}

				// ✅ Immediately trigger save after cropping
				setHasChanges(true); // optional, if you still need it tracked
				onSave(blob);
			} catch (error) {
				console.error("Cropping failed:", error);
			}
		}
	};

	// Cancel cropping mode
	const handleCropCancel = () => {
		if (fileInputRef.current) {
			fileInputRef.current.value = "";
		}
		setIsCropping(false);
		setCroppedPreview(null);
		setCroppedBlob(null);
	};

	const handleDeleteImage = () => {
		if (isTouch && touchNumber <= 1) {
			return;
		}
		setHasImage(false);
		setImageSrc(userPlaceholder);
		setHasChanges(true);
		onSave(null);
	};

	return (
		<div className="complete-picture-container">
			{/* Step 1: Show current image OR cropped preview */}
			{!isCropping && (
				<>
					<div
						className="img-container"
						{...(!isTouch && {
							onMouseEnter: () => setIsHovered(true),
							onMouseLeave: () => setIsHovered(false),
						})}
						{...(isTouch && {
							onTouchStart: () => {
								setIsHovered(true);
								setTouchNumber(touchNumber + 1);
							},
						})}
					>
						<img
							src={imageSrc}
							alt="Profile"
							className="image"
						/>
						{isHovered && (
							<div className="inside-actions">
								{(() => {
									return hasImage ? (
										<>
											<ClickIcon
												icon="edit"
												onClick={handleImageClick}
											/>
											<DeleteButton onClick={handleDeleteImage} />
										</>
									) : (
										<ClickIcon
											icon="addSquare"
											size="l"
											onClick={handleImageClick}
										/>
									);
								})()}
							</div>
						)}
					</div>
					{/* {onCancel && (
            <Button className="btn btn-primary" action={onCancel}>
              Cancel
            </Button>
          )}
          <Button className="btn btn-primary" action={handleFinalSave}>
            Save
          </Button> */}
				</>
			)}

			{/* Hidden file input */}
			<input
				type="file"
				accept="image/*"
				style={{ display: "none" }}
				ref={fileInputRef}
				onChange={handleImageChange}
			/>

			{/* Step 2: Cropping Mode */}
			{isCropping && croppedPreview && (
				<>
					<div className="crop-container">
						<Cropper
							image={croppedPreview}
							crop={crop}
							zoom={zoom}
							aspect={1}
							cropShape="round"
							cropSize={{ width: 300, height: 300 }} // ✅ This ensures a fixed circle size
							showGrid={false}
							onCropChange={setCrop}
							onZoomChange={setZoom}
							onCropComplete={onCropComplete}
						/>
					</div>

					{/* Zoom Slider */}
					<div className="controls">
						<input
							type="range"
							min={1}
							max={3}
							step={0.01}
							value={zoom}
							onChange={(e) => setZoom(Number(e.target.value))}
							className="zoom-range"
						/>

						{/* Crop control buttons */}
						<div className="buttons">
							<button onClick={handleCropCancel}>Cancel</button>
							<button onClick={handleCropConfirm}>Save</button>
						</div>
					</div>
				</>
			)}
		</div>
	);
};

export default SelectProfilePicture;
